import { createTheme, alpha } from '@mui/material/styles';

// Aviation-inspired professional color palette
const colors = {
  primary: {
    main: '#003D7A',      // Deep Aviation Blue
    light: '#1565C0',
    dark: '#002652',
    contrastText: '#FFFFFF'
  },
  secondary: {
    main: '#4A5568',      // Steel Grey
    light: '#718096',
    dark: '#2D3748',
    contrastText: '#FFFFFF'
  },
  success: {
    main: '#00FF41',      // Phosphorescent Green
    light: '#69F0AE',
    dark: '#00C853',
    contrastText: '#000000'
  },
  warning: {
    main: '#FFA500',      // Amber/Orange
    light: '#FFB74D',
    dark: '#F57C00',
    contrastText: '#000000'
  },
  error: {
    main: '#DC143C',      // Crimson Red
    light: '#EF5350',
    dark: '#B71C1C',
    contrastText: '#FFFFFF'
  },
  info: {
    main: '#00B8D4',      // Cyan
    light: '#4DD0E1',
    dark: '#0097A7',
    contrastText: '#000000'
  },
  background: {
    default: '#0F1419',   // Very Dark Blue (Cockpit Dark)
    paper: '#1A2027'      // Slightly Lighter Dark Blue
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#A0AEC0'
  }
};

// Glassmorphism styles helper
export const glassStyles = {
  glass: {
    background: 'rgba(26, 32, 39, 0.6)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
  },
  glassLight: {
    background: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
  },
  glassCard: {
    background: 'linear-gradient(135deg, rgba(26, 32, 39, 0.8) 0%, rgba(15, 20, 25, 0.9) 100%)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
  }
};

// Glow effects for status indicators
export const glowEffects = {
  new: {
    boxShadow: '0 0 10px rgba(255, 255, 255, 0.8), 0 0 20px rgba(255, 255, 255, 0.4)',
    animation: 'pulseGlow 2s ease-in-out infinite'
  },
  inProgress: {
    boxShadow: '0 0 10px rgba(255, 165, 0, 0.8), 0 0 20px rgba(255, 165, 0, 0.4)',
    animation: 'pulseGlowAmber 2s ease-in-out infinite'
  },
  critical: {
    boxShadow: '0 0 10px rgba(220, 20, 60, 0.8), 0 0 20px rgba(220, 20, 60, 0.4)',
    animation: 'pulseGlowRed 1.5s ease-in-out infinite'
  },
  success: {
    boxShadow: '0 0 10px rgba(0, 255, 65, 0.6), 0 0 20px rgba(0, 255, 65, 0.3)'
  }
};

export const aviationDarkTheme = createTheme({
  direction: 'ltr',
  palette: {
    mode: 'dark',
    ...colors,
    divider: 'rgba(255, 255, 255, 0.08)'
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Segoe UI', sans-serif",
    fontSize: 13,
    h1: {
      fontWeight: 800,
      fontSize: '2.5rem',
      letterSpacing: '-0.02em',
      lineHeight: 1.2
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.3
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.4
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.4
    },
    h5: {
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.5
    },
    h6: {
      fontWeight: 700,
      fontSize: '1rem',
      lineHeight: 1.5
    },
    body1: {
      fontSize: '0.95rem',
      lineHeight: 1.6
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em'
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 500,
      letterSpacing: '0.02em'
    },
    overline: {
      fontSize: '0.7rem',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase'
    }
  },
  shape: {
    borderRadius: 12
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background.default,
          backgroundImage: `
            radial-gradient(ellipse at 20% 0%, rgba(0, 61, 122, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 100%, rgba(0, 184, 212, 0.08) 0%, transparent 50%),
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '100% 100%, 100% 100%, 40px 40px, 40px 40px',
          backgroundAttachment: 'fixed',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.2) transparent',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255,255,255,0.3)'
            }
          }
        },
        // Glow animations
        '@keyframes pulseGlow': {
          '0%, 100%': {
            boxShadow: '0 0 5px rgba(255, 255, 255, 0.6), 0 0 10px rgba(255, 255, 255, 0.3)'
          },
          '50%': {
            boxShadow: '0 0 15px rgba(255, 255, 255, 0.9), 0 0 25px rgba(255, 255, 255, 0.5)'
          }
        },
        '@keyframes pulseGlowAmber': {
          '0%, 100%': {
            boxShadow: '0 0 5px rgba(255, 165, 0, 0.6), 0 0 10px rgba(255, 165, 0, 0.3)'
          },
          '50%': {
            boxShadow: '0 0 15px rgba(255, 165, 0, 0.9), 0 0 25px rgba(255, 165, 0, 0.5)'
          }
        },
        '@keyframes pulseGlowRed': {
          '0%, 100%': {
            boxShadow: '0 0 5px rgba(220, 20, 60, 0.6), 0 0 10px rgba(220, 20, 60, 0.3)'
          },
          '50%': {
            boxShadow: '0 0 15px rgba(220, 20, 60, 0.9), 0 0 25px rgba(220, 20, 60, 0.5)'
          }
        },
        '@keyframes shimmer': {
          '0%': { backgroundPosition: '-200% 0' },
          '100%': { backgroundPosition: '200% 0' }
        },
        '@keyframes fadeInUp': {
          '0%': {
            opacity: 0,
            transform: 'translateY(20px)'
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)'
          }
        },
        '@keyframes scaleIn': {
          '0%': {
            opacity: 0,
            transform: 'scale(0.95)'
          },
          '100%': {
            opacity: 1,
            transform: 'scale(1)'
          }
        },
        '@keyframes opacityPulse': {
          '0%, 100%': { opacity: 0.4 },
          '50%': { opacity: 1 }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: alpha(colors.background.paper, 0.8),
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        },
        elevation1: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)'
        },
        elevation2: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
        },
        elevation3: {
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, rgba(26, 32, 39, 0.9) 0%, rgba(15, 20, 25, 0.95) 100%)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.12)'
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: alpha(colors.primary.main, 0.95),
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'linear-gradient(180deg, rgba(15, 20, 25, 0.98) 0%, rgba(10, 15, 20, 1) 100%)',
          backdropFilter: 'blur(12px)',
          borderRight: '1px solid rgba(255, 255, 255, 0.06)'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '4px 8px',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            transform: 'translateX(4px)'
          },
          '&.Mui-selected': {
            backgroundColor: alpha(colors.primary.main, 0.25),
            borderLeft: `3px solid ${colors.primary.light}`,
            '&:hover': {
              backgroundColor: alpha(colors.primary.main, 0.35)
            }
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          fontWeight: 600,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          },
          '&:active': {
            transform: 'translateY(0)'
          }
        },
        contained: {
          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.35)'
          }
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.02em',
          transition: 'all 0.2s ease'
        },
        filled: {
          '&.MuiChip-colorSuccess': {
            background: `linear-gradient(135deg, ${alpha(colors.success.main, 0.2)} 0%, ${alpha(colors.success.dark, 0.3)} 100%)`,
            color: colors.success.main,
            border: `1px solid ${alpha(colors.success.main, 0.3)}`
          },
          '&.MuiChip-colorWarning': {
            background: `linear-gradient(135deg, ${alpha(colors.warning.main, 0.2)} 0%, ${alpha(colors.warning.dark, 0.3)} 100%)`,
            color: colors.warning.main,
            border: `1px solid ${alpha(colors.warning.main, 0.3)}`
          },
          '&.MuiChip-colorError': {
            background: `linear-gradient(135deg, ${alpha(colors.error.main, 0.2)} 0%, ${alpha(colors.error.dark, 0.3)} 100%)`,
            color: colors.error.light,
            border: `1px solid ${alpha(colors.error.main, 0.3)}`
          },
          '&.MuiChip-colorInfo': {
            background: `linear-gradient(135deg, ${alpha(colors.info.main, 0.2)} 0%, ${alpha(colors.info.dark, 0.3)} 100%)`,
            color: colors.info.main,
            border: `1px solid ${alpha(colors.info.main, 0.3)}`
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary.light,
                borderWidth: 2
              }
            }
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 10
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: 'rgba(255, 255, 255, 0.9)'
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.6)',
          '&.Mui-checked': {
            color: colors.info.main
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-focused': {
            color: colors.info.main
          }
        }
      }
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '& .MuiTableHead-root': {
            '& .MuiTableCell-head': {
              backgroundColor: alpha(colors.primary.main, 0.3),
              fontWeight: 700,
              fontSize: '0.8rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: colors.text.secondary,
              borderBottom: `2px solid ${alpha(colors.primary.light, 0.3)}`
            }
          },
          '& .MuiTableBody-root': {
            '& .MuiTableRow-root': {
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                '& .MuiTableCell-root': {
                  color: '#FFFFFF'
                }
              }
            }
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          padding: '12px 16px'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '0.875rem',
          textTransform: 'none',
          minWidth: 100,
          transition: 'all 0.2s ease',
          '&.Mui-selected': {
            color: '#FFFFFF'
          },
          '&:hover': {
            color: '#FFFFFF',
            opacity: 1
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
          backgroundColor: colors.primary.light,
          boxShadow: `0 0 10px ${alpha(colors.primary.light, 0.5)}`
        }
      }
    },
    MuiSkeleton: {
      defaultProps: {
        animation: 'pulse'
      },
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          animation: 'opacityPulse 1.5s ease-in-out infinite',
          '&::after': {
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent)'
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(colors.background.paper, 0.95),
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 8,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '8px 12px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
        },
        arrow: {
          color: alpha(colors.background.paper, 0.95)
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'linear-gradient(135deg, rgba(26, 32, 39, 0.98) 0%, rgba(15, 20, 25, 0.99) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 16,
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.5)'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundImage: 'linear-gradient(135deg, rgba(26, 32, 39, 0.98) 0%, rgba(15, 20, 25, 0.99) 100%)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 6px',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.06)'
          },
          '&.Mui-selected': {
            backgroundColor: alpha(colors.primary.main, 0.2),
            '&:hover': {
              backgroundColor: alpha(colors.primary.main, 0.3)
            }
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backdropFilter: 'blur(8px)'
        },
        standardError: {
          backgroundColor: alpha(colors.error.main, 0.15),
          border: `1px solid ${alpha(colors.error.main, 0.3)}`
        },
        standardWarning: {
          backgroundColor: alpha(colors.warning.main, 0.15),
          border: `1px solid ${alpha(colors.warning.main, 0.3)}`
        },
        standardSuccess: {
          backgroundColor: alpha(colors.success.main, 0.15),
          border: `1px solid ${alpha(colors.success.main, 0.3)}`
        },
        standardInfo: {
          backgroundColor: alpha(colors.info.main, 0.15),
          border: `1px solid ${alpha(colors.info.main, 0.3)}`
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }
      }
    },
    MuiBadge: {
      styleOverrides: {
        dot: {
          boxShadow: '0 0 8px currentColor'
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
          backgroundColor: 'rgba(255, 255, 255, 0.1)'
        },
        bar: {
          borderRadius: 4
        }
      }
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round'
          }
        }
      }
    }
  }
});
